import { keepPreviousData } from '@tanstack/react-query';
import { HttpHeaders } from '../common/queryClient/getMutationFn';
import {
  LockResponse,
  ObjectLockId,
  useLockMutation,
  useObjectLockQuery,
} from './query/useLockQuery';
import { ApiErrorResponse } from '../common/ApiClient';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './useLocks.translations.json';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { ObjectType } from './types';
import { Callback, useInterval } from '../common/useInterval';
import { getUserName } from '../user';

const { addTranslations, t } = i18nNsFactory('use-locks');
addTranslations(translations);

export function lockingErrorText(error: ApiErrorResponse | null) {
  if (!error) {
    return null;
  }

  const lockingError = error?.response?.data?.errors?.[0];
  const code = lockingError?.code;
  if (code) {
    const options: Record<string, string> = {};
    const args = lockingError?.meta?.args;
    if (code === 'object_locked' && Array.isArray(args)) {
      const [objectType, username, firstName, lastName] = args;
      if (username) {
        options['user'] = getUserName({ firstName, lastName, username });
      }
      if (objectType) {
        options['context'] = objectType;
      }
    }

    return t(`error.${code}`, options);
  }

  return t('error.unknown');
}

const LOCK_INFO_POLLING_INTERVAL = 25_000;
// Lock duration is 2 minutes; extend lock 10s before it is released.
const LOCK_EXTENSION_INTERVAL = 110_000;

export function isLocked(lockData?: LockResponse) {
  return !lockData || (lockData.lock && !lockData.owner);
}

export function useShowLockData(
  objectType: ObjectType,
  objectId?: number,
  discriminator?: string,
  additionalLocks?: ObjectLockId[]
) {
  const request = {
    lockId: {
      objectId: objectId ?? 0,
      objectType,
      discriminator,
    },
    additionalLocks,
  };
  const {
    data: lockData,
    error,
    refetch,
  } = useObjectLockQuery(request, {
    enabled: !!objectId,
    placeholderData: keepPreviousData,
    refetchInterval: LOCK_INFO_POLLING_INTERVAL,
  });

  const errorMessage = useMemo(() => {
    return lockingErrorText(error);
  }, [error]);

  return { error: errorMessage, lockData, refetchLockData: refetch };
}

export function useApplyEditLock(
  objectType: ObjectType,
  objectId?: number,
  discriminator?: string,
  additionalLocks?: ObjectLockId[],
  checkLocks?: ObjectLockId[]
) {
  const releaseLockRef = useRef<Callback>(undefined);
  const { mutate, data: lockData, error } = useLockMutation();

  const extendLock = useCallback(() => {
    if (objectId) {
      mutate({
        id: objectId,
        data: {
          lockId: {
            objectId,
            objectType: objectType,
            discriminator: discriminator ?? undefined,
          },
          additionalLocks,
          checkLocks,
        },
      });
    }
  }, [
    objectId,
    mutate,
    objectType,
    discriminator,
    additionalLocks,
    checkLocks,
  ]);
  useInterval(extendLock, LOCK_EXTENSION_INTERVAL, true);

  useEffect(() => {
    releaseLockRef.current = () => {
      if (objectId) {
        const headers: HttpHeaders = {
          objectType: objectType,
          ...(discriminator !== undefined ? { discriminator } : {}),
        };
        if (additionalLocks) {
          headers.additionalLocks = additionalLocks.map((lock) =>
            [lock.objectType, lock.objectId, lock.discriminator]
              .filter((part) => !!part)
              .join('-')
          );
        }
        mutate({
          id: objectId,
          headers,
        });
      }
    };
  }, [additionalLocks, discriminator, mutate, objectId, objectType]);
  useEffect(() => {
    return () => releaseLockRef.current?.();
  }, []);

  const errorMessage = useMemo(() => {
    return lockingErrorText(error);
  }, [error]);

  return { error: errorMessage, lockData: lockData ?? undefined };
}
